import { useRuntimeConfig, useState } from '#imports'

import {
  Identify as AmplitudeIdentify,
  Types as AmplitudeTypes,
  flush as amplitudeFlush,
  getDeviceId as amplitudeGetDeviceId,
  getSessionId as amplitudeGetSessionId,
  identify as amplitudeIdentify,
  init as amplitudeInit,
  logEvent as amplitudeLogEvent,
  setOptOut as amplitudeSetOptOut,
  setTransport as amplitudeSetTransport,
  setUserId as amplitudeSetUserId,
} from '@amplitude/analytics-browser'

import { useDisableTrackers } from '../composables/useDisableTrackers'

import {
  AMPLITUDE_DEVICE_ID_STATE_KEY,
  AMPLITUDE_SESSION_ID_STATE_KEY,
  FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
} from './constants'
import type { UserProperties } from './types'

// BM is under the "growth plan" of Amplitude
// Documentation: https://amplitude.github.io/Amplitude-JavaScript/

const isUnitTest = process.env.VITEST === 'true'

function disableAmplitudeEvents() {
  const trackersDisabled = useDisableTrackers()

  return isUnitTest || trackersDisabled
}

export const sendEvent = (eventName: string, payload: object) => {
  if (disableAmplitudeEvents()) return

  amplitudeLogEvent(eventName, payload)
}

export function setUserProperties(userProperties: UserProperties) {
  if (disableAmplitudeEvents()) return

  const identify = new AmplitudeIdentify()

  Object.keys(userProperties).forEach((propertyName) => {
    identify.set(propertyName, userProperties[propertyName])
  })

  amplitudeIdentify(identify)
}

export const setUserId = (userId: string) => {
  if (disableAmplitudeEvents()) return

  amplitudeSetUserId(userId)
}

export const getDeviceId = () => {
  if (disableAmplitudeEvents()) return ''

  return amplitudeGetDeviceId()
}

export function init(initialValues?: { deviceId?: string }) {
  const {
    public: { tracking: config },
  } = useRuntimeConfig()

  amplitudeInit(config.amplitude.apiKey, {
    appVersion: config.version,
    flushIntervalMillis: 5000,
    logLevel: AmplitudeTypes.LogLevel.None,
    deviceId: initialValues?.deviceId,
    autocapture: {
      attribution: true,
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },

    // serverZone is fixed to EU in order to keep the data out of the US.
    serverZone: 'EU',
  }).promise.then(() => {
    // Store the device id Amplitude is currently using (forwarded header value if any, or Amplitude-generated id)
    // so consumers can use it (e.g. the shopping assistant forward it as a request header)
    const amplitudeDeviceIdState = useState<string | undefined>(
      AMPLITUDE_DEVICE_ID_STATE_KEY,
    )
    const amplitudeSessionIdState = useState<number | undefined>(
      AMPLITUDE_SESSION_ID_STATE_KEY,
    )

    const resolvedDeviceId = amplitudeGetDeviceId()
    if (resolvedDeviceId) {
      amplitudeDeviceIdState.value = resolvedDeviceId
    }

    const resolvedSessionId = amplitudeGetSessionId()
    if (resolvedSessionId) {
      amplitudeSessionIdState.value = resolvedSessionId
    }
  })

  // this function is invoked when page is unloaded
  // switches to beacon and sends all unsent events immediately
  // see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/migration/?h=onexitpage#flush-or-onexitpage
  window.addEventListener('pagehide', () => {
    // Set https transport to use sendBeacon API
    amplitudeSetTransport('beacon')
    // Send all pending events to server
    amplitudeFlush()
  })
}

export const setOptIn = (optIn: boolean) => {
  if (disableAmplitudeEvents()) return

  if (optIn) {
    // Only honor the device id forwarded from the mobile webview header.
    // Outside that case, let Amplitude keep its persisted/generated id.
    const deviceId = useState<string | undefined>(
      FORWARDED_AMPLITUDE_DEVICE_ID_STATE_KEY,
    ).value
    init({ deviceId })
  }

  amplitudeSetOptOut(!optIn)
}
